import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { FeedbackRecognitionType } from 'Shared/constants/types';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { fetchSingleFeedback } from 'Shared/actions/Feedbacks.actions';

// Selectors
import { getFeedbackByUuidV2 } from 'Shared/selectors/Feedbacks.selectors';

// Components
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButton } from 'Wpds/components/button';
import KudoItem from './KudoItem';
import KudoItemLoading from './KudoItemLoading';
import ItemDoesntExistError from 'Shared/components/ItemDoesntExistError/ItemDoesntExistError';

type Props = {
  isOpen: boolean;
  toggleIsOpen: () => void;
  feedbackUuid?: string;
};

const KudoItemModal = ({ isOpen, toggleIsOpen, feedbackUuid }: Props) => {
  const dispatch = useDispatch();

  const [isLoading, toggleIsLoading] = useToggle(true);
  const [hasError, toggleHasError] = useToggle(false);

  const feedback: FeedbackRecognitionType = useSelector(state => getFeedbackByUuidV2(state, { feedbackUuid }));

  useEffect(() => {
    if (feedbackUuid)
      dispatch(fetchSingleFeedback(feedbackUuid)).then(response => {
        toggleIsLoading();
        if (!response) toggleHasError();
      });
  }, [feedbackUuid]);

  if (!feedbackUuid) return null;
  if (!isOpen) return null;

  return (
    <WpModal isOpen={isOpen} onToggle={toggleIsOpen} size="sm">
      <WpModalHeader />
      {isLoading ? (
        <WpModalBody>
          <KudoItemLoading />
        </WpModalBody>
      ) : (
        <>
          {hasError ? (
            <WpModalBody>
              <ItemDoesntExistError
                title="We couldn’t find this kudo."
                description="You either don't have access to view this kudo, or it's been deleted."
              />
            </WpModalBody>
          ) : (
            <WpModalBody>{feedback && <KudoItem feedback={feedback} />}</WpModalBody>
          )}
        </>
      )}
      <WpModalFooter>
        <WpButton variant="tertiary" onClick={toggleIsOpen}>
          Close
        </WpButton>
      </WpModalFooter>
    </WpModal>
  );
};

export default KudoItemModal;
